import { type Address } from "viem"
import { type ChainId } from "@/web3/constants/chains"

// Type declarations for DRS system
export type DRSConfig = {
  name: string
  chainId: ChainId
  delegatesEligibilityOracleAddress?: Address
  defaultThreshold: number
  provider: DRSProviderType
  profileBaseUrl: string
  showInfoCard?: boolean
  infoCardDisplayName?: string
  docs?: {
    drsCalculationLink?: string
    forumLinkAccountUrl?: string
    autoDelegateInfoLink?: string
  }
  getDRSStatusTooltip?: (drsInfo: DRSInfo, threshold: number) => string
  displayStatusBadge: boolean
}

/**
 * Delegate status in the DRS system
 * - ACTIVE: Delegate meets the threshold requirements
 * - INACTIVE: Delegate does not meet threshold requirements
 * - GHOST: Delegate has voting power but has never voted
 */
export enum DRSStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
  GHOST = "GHOST",
}

/**
 * Supported DRS provider types
 */
export enum DRSProviderType {
  KARMA = "karma",
  CURIA = "curia",
}

/**
 * DRS information for a delegate
 */
export type DRSInfo = {
  /** Ethereum address (lowercase) */
  address: string
  /** Current delegate status */
  status: DRSStatus
  /** Delegate reputation score */
  drsScore: number
  /** Whether provider supports probation status */
  probationSupported: boolean
  /** Whether delegate is on probation (new delegate) */
  isProbation: boolean
  /** Last time the score was updated */
  lastUpdated: string | null
  /** External link to delegate profile */
  externalLink: string
  /** Provider that generated this info */
  provider: DRSProviderType
  /** Whether delegate is eligible based on threshold */
  isEligible: boolean
  /** Whether we have actual DRS data (vs empty/default) */
  hasInfo: boolean
  /** Delegate username */
  username: string | null
}

/**
 * DRS Provider interface
 */
export interface DRSProvider {
  getDRSInfo(address: string): Promise<DRSInfo | null>
  getBulkDRSInfo(addresses: string[]): Promise<Map<string, DRSInfo>>
  initialize(): Promise<DRSProvider>
}

/**
 * Karma API response type
 */
export type KarmaApiResponse = {
  publicAddress: string
  daoName: string
  ensName: string | null
  twitterHandle: string | null
  discourseHandles: string | null
  isForumVerified: boolean
  isNominee: boolean
  score: number
  daos: string
  poap: number
  daoCount: number
  userType: string
  pitch: string | null
  stats: Array<{
    period: string
    karmaScore: number
    karmaRank: number
    forumActivityScore: number | null
    forumLikesReceived: number | null
    forumPostsReadCount: number | null
    proposalsInitiated: number | null
    proposalsDiscussed: number | null
    forumTopicCount: number | null
    forumPostCount: number | null
    offChainVotesPct: number | null
    onChainVotesPct: number | null
    updatedAt: string
    createdAt: string
    percentile: number | null
    gitcoinHealthScore: number | null
    deworkTasksCompleted: number | null
    deworkPoints: number | null
    proposalsOnSnapshot: number | null
    discordScore: number
    discordMessagesCount: number
    discordMessagePercentile: number
    proposalsOnAragon: number | null
    aragonVotesPct: number | null
  }>
  delegatorCount: number
  delegatedVotes: string
  snapshotDelegatedVotes: string | null
  voteWeight: string
  firstTokenDelegatedAt: string
  joinDateAt: string
  workstreams: Array<unknown>
  scoreBreakdown: Array<{
    name: string
    score: number
    pct: number
  }>
  aboutMe: string | null
  profilePicture: string | null
  website: string | null
  realName: string | null
  delegatePitch: string | null
  status: string
  discordHandle: string | null
  discordUsername: string | null
  roles: Array<unknown>
  userCreatedAt: string
  acceptedTOS: boolean
  termsOfAgreement: string | null
  tracks: Array<unknown>
  discussionThread: string | null
}

/**
 * Curia API response type
 */
export type CuriaApiResponse = {
  address: string
  status: string
  username: string | null
  delegate_score: number
  is_probation: boolean
  latest_updated_at?: string
}
